import Request from 'utils/request';
import Config from 'config/Config';
import { Toast } from 'antd-mobile';
import moment from 'moment';

const memCache = new Map(); // activityId -> { activityName, effectiveTime }
const inflight = new Map(); // activityId -> Promise

function getCacheKey(activityId) {
  return `activity_info_${activityId}`;
}

function formatToMinute(effectiveTime) {
  if (!effectiveTime) return '';
  const raw = String(effectiveTime).trim();
  if (!raw) return '';

  // Try strict parse first for the common formats we expect from backend.
  const strict = moment(raw, ['YYYY-MM-DD HH:mm:ss', 'YYYY-MM-DD HH:mm', moment.ISO_8601], true);
  if (strict.isValid()) return strict.format('YYYY-MM-DD HH:mm');

  // Fallback: non-strict parse (covers "YYYY/MM/DD HH:mm:ss" etc).
  const loose = moment(raw);
  return loose.isValid() ? loose.format('YYYY-MM-DD HH:mm') : raw;
}

async function fetchEffectiveTime(activityId) {
  if (!activityId) return { activityName: '', effectiveTime: '' };
  const id = String(activityId);

  if (memCache.has(id)) return memCache.get(id) || { activityName: '', effectiveTime: '' };
  try {
    const cached = sessionStorage.getItem(getCacheKey(id));
    if (cached) {
      const parsed = JSON.parse(cached);
      memCache.set(id, parsed);
      return parsed;
    }
  } catch (e) {
    // ignore sessionStorage read failure
  }

  if (inflight.has(id)) return inflight.get(id);

  const p = Request({
    url: `promotion/activity/id/${id}`,
    host: Config.HOST_MALL,
    method: 'get',
    ignoreErr: true
  })
    .then((res) => {
      const data = (res && res.data) || {};
      const baseReqDto = data.baseReqDto || {};
      const result = {
        activityName: baseReqDto.activityName || '',
        effectiveTime: formatToMinute(baseReqDto.effectiveTime || '')
      };
      if (result.effectiveTime) {
        memCache.set(id, result);
        try {
          sessionStorage.setItem(getCacheKey(id), JSON.stringify(result));
        } catch (e) {
          // ignore sessionStorage write failure
        }
      }
      return result;
    })
    .catch(() => ({ activityName: '', effectiveTime: '' }))
    .finally(() => {
      inflight.delete(id);
    });

  inflight.set(id, p);
  return p;
}

function rememberActivityInfo(activityId, activityName, effectiveTime) {
  if (!activityId) return;
  const id = String(activityId);
  const result = { activityName: activityName || '', effectiveTime: effectiveTime || '' };
  memCache.set(id, result);
  try {
    sessionStorage.setItem(getCacheKey(id), JSON.stringify(result));
  } catch (e) {
    // ignore sessionStorage write failure
  }
}

/**
 * 活动未开始时的统一 Toast。
 * @param {string|number} activityId
 * @param {{ activityName?: string, effectiveTime?: string, activityData?: { baseReqDto?: { activityName?: string, effectiveTime?: string } } }} [options]
 *   - 已调用 `promotion/activity/id` 时传入 `activityData`：只使用该对象上的字段，不会再请求接口（无字段则走兜底文案）。
 *   - 或显式传入 `activityName` / `effectiveTime`。
 */
export async function toastActivityNotStarted(activityId, options) {
  const opts = options && typeof options === 'object' ? options : {};
  let activityName;
  let effectiveTime;

  // activityName 取值逻辑
  if (opts.activityName !== undefined) {
    activityName = opts.activityName;
  } else if (Object.prototype.hasOwnProperty.call(opts, 'activityData')) {
    activityName =
      opts.activityData && opts.activityData.baseReqDto && opts.activityData.baseReqDto.activityName
        ? opts.activityData.baseReqDto.activityName
        : '';
  }

  // effectiveTime 取值逻辑
  if (opts.effectiveTime !== undefined) {
    effectiveTime = opts.effectiveTime;
  } else if (Object.prototype.hasOwnProperty.call(opts, 'activityData')) {
    effectiveTime =
      opts.activityData &&
      opts.activityData.baseReqDto &&
      opts.activityData.baseReqDto.effectiveTime
        ? opts.activityData.baseReqDto.effectiveTime
        : '';
  }

  // 如果 activityName 或 effectiveTime 未通过上述方式获取，则请求接口
  if (activityName === undefined || effectiveTime === undefined) {
    const result = await fetchEffectiveTime(activityId);
    if (activityName === undefined) {
      activityName = result.activityName;
    }
    if (effectiveTime === undefined) {
      effectiveTime = result.effectiveTime;
    }
  }

  if (effectiveTime) {
    const formatted = formatToMinute(effectiveTime);
    rememberActivityInfo(activityId, activityName, formatted);

    // 计算倒计时
    const targetMoment = moment(formatted, 'YYYY-MM-DD HH:mm');
    const nowMoment = moment();
    let countdownText = '';

    if (targetMoment.isSame(nowMoment, 'day')) {
      // 同一天，显示倒计时x时x分
      const diffMinutes = targetMoment.diff(nowMoment, 'minutes');
      const hours = Math.floor(diffMinutes / 60);
      const minutes = diffMinutes % 60;
      countdownText = `倒计时${hours}时${minutes}分`;
    } else {
      // 不同天，显示倒计时x天（按自然日计算）
      const diffDays = targetMoment.startOf('day').diff(nowMoment.startOf('day'), 'days');
      countdownText = `倒计时${diffDays}天`;
    }

    Toast.info(`${activityName || ''}活动将于${formatted} 暖心开启，${countdownText}敬请期待哦！`);
  } else {
    Toast.info('活动将于稍后暖心开启，\n敬请期待～');
  }
}



// WEBPACK FOOTER //
// ./src/utils/activityTips.js